import { useMemo } from 'react'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Typography,
} from '@mui/material'
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro'
import { TransitionProps } from '@mui/material/transitions'
import React from 'react'
import { LedgerBreakdownRow, LedgerOverviewRow } from '../../../types/osp-ledger'
import palette from '../../../theme/palette'
import { baseGridSx } from './gridStyles'

type AbsenceLedgerModalProps = {
  open: boolean
  onClose: () => void
  overviewRows?: LedgerOverviewRow[]
  breakdownRows?: LedgerBreakdownRow[]
  loading?: boolean
  hasSplitPots?: boolean
  showDiscretionaryPay?: boolean
}

const Transition = React.forwardRef(
  (
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
)

const formatValue = (value: number) => value.toFixed(2)

const getUsedHoursColor = (value: number, isNonWorkingDay: boolean) => {
  if (isNonWorkingDay) return palette.type.multipleRequests.color
  if (value < 0) return palette.type.absence.color
  return 'rgba(0,0,0,0.98)'
}

const getBalanceColor = (value: number, isNonWorkingDay: boolean) => {
  if (isNonWorkingDay) return palette.type.multipleRequests.color
  if (value < 0) return palette.type.absence.color
  return 'rgba(0,0,0,0.98)'
}

const getRateColor = (value: number, isNonWorkingDay: boolean, rateColor: string) => {
  if (isNonWorkingDay) return palette.type.multipleRequests.color
  if (value === 0) return 'rgba(0,0,0,0.98)'
  return rateColor
}

const overviewHeaderSx = {
  '& .hundred-percent-header': {
    backgroundColor: '#EFF6FF',
    '& .MuiDataGrid-columnHeaderTitle': {
      color: '#1565C0',
    },
  },
  '& .fifty-percent-header': {
    backgroundColor: '#FDF7FF',
    '& .MuiDataGrid-columnHeaderTitle': {
      color: '#7B1FA2',
    },
  },
}

const breakdownHeaderSx = {
  '& .hundred-percent-header': {
    backgroundColor: '#EFF6FF',
    '& .MuiDataGrid-columnHeaderTitle': {
      color: '#1565C0',
    },
  },
  '& .fifty-percent-header': {
    backgroundColor: '#FDF7FF',
    '& .MuiDataGrid-columnHeaderTitle': {
      color: '#7B1FA2',
    },
  },
  '& .adjustment-row': {
    backgroundColor: 'rgba(196,234,171,0.16)',
    '&:hover': {
      backgroundColor: 'rgba(196,234,171,0.16)',
    },
  },
}

function AbsenceLedgerModal({
  open,
  onClose,
  overviewRows = [],
  breakdownRows = [],
  loading = false,
  hasSplitPots = true,
  showDiscretionaryPay = true,
}: AbsenceLedgerModalProps) {
  const overviewColumns: GridColDef<LedgerOverviewRow>[] = useMemo(
    () => {
      if (hasSplitPots) {
        return ([
          {
            field: 'absencePeriod',
            headerName: 'Absence Period',
            flex: 1,
            minWidth: 175,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
          },
          {
            field: 'hours',
            headerName: 'Hours',
            width: 74,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ value }) => formatValue(value),
          },
          {
            field: 'hundredPercentUsed',
            headerName: '100% Rate Used',
            flex: 1,
            minWidth: 155,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'hundred-percent-header',
            sortable: false,
            renderCell: ({ value }) => (
              <span style={{ color: '#1565C0' }}>{formatValue(value)}</span>
            ),
          },
          {
            field: 'fiftyPercentUsed',
            headerName: '50% Rate Used',
            flex: 1,
            minWidth: 155,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'fifty-percent-header',
            sortable: false,
            renderCell: ({ value }) => (
              <span style={{ color: '#7B1FA2' }}>{formatValue(value)}</span>
            ),
          },
          {
            field: 'discretionaryPay',
            headerName: 'Discretionary Pay',
            width: 158,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ value }) => value,
          },
          {
            field: 'unpaid',
            headerName: 'Unpaid',
            width: 89,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ value }) => formatValue(value),
          },
        ] as GridColDef<LedgerOverviewRow>[]).filter(col => showDiscretionaryPay || col.field !== 'discretionaryPay')
      }

      // Single pot — 5 columns, all flex to fill the space evenly
      return ([
        {
          field: 'absencePeriod',
          headerName: 'Absence Period',
          flex: 1,
          minWidth: 175,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
        },
        {
          field: 'hours',
          headerName: 'Hours',
          flex: 1,
          minWidth: 74,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ value }) => formatValue(value),
        },
        {
          field: 'hundredPercentUsed',
          headerName: 'Sick Pay Used',
          flex: 1,
          minWidth: 155,
          headerAlign: 'center',
          align: 'center',
          headerClassName: 'hundred-percent-header',
          sortable: false,
          renderCell: ({ value }) => (
            <span style={{ color: '#1565C0' }}>{formatValue(value)}</span>
          ),
        },
        {
          field: 'discretionaryPay',
          headerName: 'Discretionary Pay',
          flex: 1,
          minWidth: 158,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ value }) => value,
        },
        {
          field: 'unpaid',
          headerName: 'Unpaid',
          flex: 1,
          minWidth: 89,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ value }) => formatValue(value),
        },
      ] as GridColDef<LedgerOverviewRow>[]).filter(col => showDiscretionaryPay || col.field !== 'discretionaryPay')
    },
    [hasSplitPots, showDiscretionaryPay]
  )

  const breakdownColumns: GridColDef<LedgerBreakdownRow>[] = useMemo(
    () => {
      if (hasSplitPots) {
        return ([
          {
            field: 'date',
            headerName: 'Date',
            width: 105,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => (
              <span style={{ color: row.isNonWorkingDay ? palette.type.multipleRequests.color : 'rgba(0,0,0,0.98)' }}>
                {row.date}
              </span>
            ),
          },
          {
            field: 'usedHours',
            headerName: 'Used Hours',
            width: 120,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => {
              if (row.isAdjustmentRow) return ''
              return (
                <span style={{ color: getUsedHoursColor(row.usedHours, row.isNonWorkingDay) }}>
                  {formatValue(row.usedHours)}
                </span>
              )
            },
          },
          {
            field: 'hundredPercentUsed',
            headerName: '100% Rate',
            flex: 1,
            minWidth: 140,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'hundred-percent-header',
            sortable: false,
            renderCell: ({ row }) => {
              if (row.isAdjustmentRow) {
                if (row.hundredPercentUsed <= 0) return ''
                return (
                  <span style={{ color: '#1565C0' }}>
                    +{formatValue(row.hundredPercentUsed)}
                  </span>
                )
              }
              return (
                <span
                  style={{
                    color: getRateColor(row.hundredPercentUsed, row.isNonWorkingDay, '#1565C0'),
                  }}
                >
                  {formatValue(row.hundredPercentUsed)}
                </span>
              )
            },
          },
          {
            field: 'fiftyPercentUsed',
            headerName: '50% Rate',
            flex: 1,
            minWidth: 140,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'fifty-percent-header',
            sortable: false,
            renderCell: ({ row }) => {
              if (row.isAdjustmentRow) {
                if (row.fiftyPercentUsed <= 0) return ''
                return (
                  <span style={{ color: '#7B1FA2' }}>
                    +{formatValue(row.fiftyPercentUsed)}
                  </span>
                )
              }
              return (
                <span
                  style={{
                    color: getRateColor(row.fiftyPercentUsed, row.isNonWorkingDay, '#7B1FA2'),
                  }}
                >
                  {formatValue(row.fiftyPercentUsed)}
                </span>
              )
            },
          },
          {
            field: 'balance100',
            headerName: 'Balance (100%)',
            flex: 1,
            minWidth: 140,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => (
              <span style={{ color: getBalanceColor(row.balance100, row.isNonWorkingDay) }}>
                {formatValue(row.balance100)}
              </span>
            ),
          },
          {
            field: 'balance50',
            headerName: 'Balance (50%)',
            flex: 1,
            minWidth: 140,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => (
              <span style={{ color: row.isNonWorkingDay ? palette.type.multipleRequests.color : 'rgba(0,0,0,0.98)' }}>
                {formatValue(row.balance50)}
              </span>
            ),
          },
          {
            field: 'discretionaryPay',
            headerName: 'Discretionary Pay',
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => {
              if (row.isAdjustmentRow) return ''
              return (
                <span style={{ color: row.isNonWorkingDay ? palette.type.multipleRequests.color : 'rgba(0,0,0,0.98)' }}>
                  {row.discretionaryPay}
                </span>
              )
            },
          },
          {
            field: 'unpaid',
            headerName: 'Unpaid',
            width: 89,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => {
              if (row.isAdjustmentRow) return ''
              return (
                <span style={{ color: row.isNonWorkingDay ? palette.type.multipleRequests.color : 'rgba(0,0,0,0.98)' }}>
                  {formatValue(row.unpaid)}
                </span>
              )
            },
          },
        ] as GridColDef<LedgerBreakdownRow>[]).filter(col => showDiscretionaryPay || col.field !== 'discretionaryPay')
      }

      // Single pot — 6 columns, all flex to fill the space evenly
      return ([
        {
          field: 'date',
          headerName: 'Date',
          flex: 1,
          minWidth: 105,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ row }) => (
            <span style={{ color: row.isNonWorkingDay ? palette.type.multipleRequests.color : 'rgba(0,0,0,0.98)' }}>
              {row.date}
            </span>
          ),
        },
        {
          field: 'usedHours',
          headerName: 'Used Hours',
          flex: 1,
          minWidth: 100,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ row }) => {
            if (row.isAdjustmentRow) return ''
            return (
              <span style={{ color: getUsedHoursColor(row.usedHours, row.isNonWorkingDay) }}>
                {formatValue(row.usedHours)}
              </span>
            )
          },
        },
        {
          field: 'hundredPercentUsed',
          headerName: 'Sick Pay Hours',
          flex: 1,
          minWidth: 140,
          headerAlign: 'center',
          align: 'center',
          headerClassName: 'hundred-percent-header',
          sortable: false,
          renderCell: ({ row }) => {
            if (row.isAdjustmentRow) {
              if (row.hundredPercentUsed <= 0) return ''
              return (
                <span style={{ color: '#1565C0' }}>
                  +{formatValue(row.hundredPercentUsed)}
                </span>
              )
            }
            return (
              <span style={{ color: getRateColor(row.hundredPercentUsed, row.isNonWorkingDay, '#1565C0') }}>
                {formatValue(row.hundredPercentUsed)}
              </span>
            )
          },
        },
        {
          field: 'balance100',
          headerName: 'Sick Pay Balance',
          flex: 1,
          minWidth: 140,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ row }) => (
            <span style={{ color: getBalanceColor(row.balance100, row.isNonWorkingDay) }}>
              {formatValue(row.balance100)}
            </span>
          ),
        },
        {
          field: 'discretionaryPay',
          headerName: 'Discretionary Pay',
          flex: 1,
          minWidth: 158,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ row }) => {
            if (row.isAdjustmentRow) return ''
            return (
              <span style={{ color: row.isNonWorkingDay ? palette.type.multipleRequests.color : 'rgba(0,0,0,0.98)' }}>
                {row.discretionaryPay}
              </span>
            )
          },
        },
        {
          field: 'unpaid',
          headerName: 'Unpaid',
          flex: 1,
          minWidth: 89,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          renderCell: ({ row }) => {
            if (row.isAdjustmentRow) return ''
            return (
              <span style={{ color: row.isNonWorkingDay ? palette.type.multipleRequests.color : 'rgba(0,0,0,0.98)' }}>
                {formatValue(row.unpaid)}
              </span>
            )
          },
        },
      ] as GridColDef<LedgerBreakdownRow>[]).filter(col => showDiscretionaryPay || col.field !== 'discretionaryPay')
    },
    [hasSplitPots, showDiscretionaryPay]
  )

  const BREAKDOWN_HEADER_HEIGHT = 48
  const BREAKDOWN_ROW_HEIGHT = 40
  const BREAKDOWN_MAX_HEIGHT = 420
  const breakdownGridHeight = Math.min(
    BREAKDOWN_HEADER_HEIGHT + breakdownRows.length * BREAKDOWN_ROW_HEIGHT + 2,
    BREAKDOWN_MAX_HEIGHT
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="lg"
      fullWidth
      data-testid="absence-ledger-modal"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 500,
          fontSize: '22px',
          lineHeight: '20px',
          letterSpacing: '0.15px',
          color: '#4A4A4A',
          padding: '24px',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        Absence Ledger
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        {/* Overview Grid */}
        <Box
          sx={{
            backgroundColor: '#FAFAFA',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            padding: '16px',
          }}
        >
          <DataGridPro
            rows={overviewRows}
            columns={overviewColumns}
            autoHeight
            hideFooter
            disableColumnMenu
            disableColumnReorder
            disableColumnResize
            disableSelectionOnClick
            headerHeight={BREAKDOWN_HEADER_HEIGHT}
            rowHeight={BREAKDOWN_ROW_HEIGHT}
            loading={loading}
            sx={{ ...baseGridSx, ...overviewHeaderSx }}
          />
        </Box>

        {/* Breakdown Section */}
        <Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.1px',
              color: 'rgba(0,0,0,0.64)',
            }}
          >
            Shows the daily hours and resulting balance.
          </Typography>

          <Box sx={{ height: breakdownGridHeight }}>
            <DataGridPro
              rows={breakdownRows}
              columns={breakdownColumns}
              hideFooter
              disableColumnMenu
              disableColumnReorder
              disableColumnResize
              disableSelectionOnClick
              headerHeight={BREAKDOWN_HEADER_HEIGHT}
              rowHeight={BREAKDOWN_ROW_HEIGHT}
              loading={loading}
              getRowClassName={({ row }) => (row.isAdjustmentRow ? 'adjustment-row' : '')}
              sx={{
                ...baseGridSx,
                ...breakdownHeaderSx,
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto',
                  borderTop: '1px solid rgba(0,0,0,0.1)',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#FAFAFA',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          padding: '16px',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          component="button"
          onClick={onClose}
          data-testid="absence-ledger-close-button"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            color: '#3C4A44',
            backgroundColor: 'transparent',
            border: '1px solid #BBCAC3',
            borderRadius: '100px',
            padding: '16px 32px',
            cursor: 'pointer',
            outline: 'none',
            '&:hover': {
              backgroundColor: 'rgba(60, 74, 68, 0.04)',
            },
          }}
        >
          Close
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default AbsenceLedgerModal
